import React, { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "react-query";

import { getJobStatus } from "../apis/getJobStatus";
import { GeoCalculationsJob } from "../models/GeoCalculations";
import { GeoModelJobStatus } from "../models/GeoModelJob";
import { isInProgressStatus } from "../utils/jobStatusTaxonomy";

// Terminal statuses that carry payload (errorMessage / invalidItems /
// lastSuccessfulRunDate) living on the jobs-list query rather than this status
// poll. Transitioning into any of these must refetch that list so the details
// shown alongside the pill (reason, partial counts, last run date) aren't stale.
const SETTLED_WITH_PAYLOAD: string[] = [
  GeoModelJobStatus.Error,
  GeoModelJobStatus.PartiallyCompleted,
  GeoModelJobStatus.Completed
];

// A job is worth polling only while it's actively in progress (allowlisted
// backend string — see jobStatusTaxonomy) or we've never fetched its status
// yet. A settled or unrecognized/unknown status won't change on its own, so we
// stop hitting the endpoint. Re-running a job optimistically sets an
// in-progress status (see the calc table's calculate()), which resumes polling.
const shouldPoll = (status?: string) =>
  status === undefined || isInProgressStatus(status);

// After this many consecutive poll failures for a job with no known status
// yet, stop polling it and surface "Unknown" instead of spinning on
// "Checking…" forever.
const MAX_CONSECUTIVE_FAILURES = 4;

export function useJobStatus(
  jobs: GeoCalculationsJob[],
  jobStatus: { [key: string]: string },
  setJobStatus: React.Dispatch<
    React.SetStateAction<{
      [key: string]: string;
    }>
  >
) {
  const queryClient = useQueryClient();

  // The caller's current status map, mirrored into a ref so the interval can
  // read the latest value (incl. an optimistic re-run status) without being in
  // the effect's dependency list — which would tear the interval down every tick.
  const externalStatus = useRef(jobStatus);
  externalStatus.current = jobStatus;

  // The hook's own record of the last status it polled per job — drives the
  // terminal-skip and "only update on change" decisions. Kept separate from the
  // caller's map so it stays correct regardless of when React re-renders.
  const knownStatus = useRef<{ [key: string]: string }>({});

  // Consecutive poll-failure count per job, reset on any success. Only tracked
  // for jobs that still have no known status.
  const consecutiveFailures = useRef<{ [key: string]: number }>({});

  // Job ids with a status request currently in flight, so a response slower than
  // the 2s interval doesn't overlap the next tick (which could let a stale
  // response clobber a newer status).
  const inFlight = useRef<Set<string>>(new Set());

  // Only re-subscribe when the SET of job ids changes — not when a status
  // updates or the jobs list is refetched with the same ids (a new array
  // identity). Prevents the interval from resetting and re-firing constantly.
  const jobIdsKey = useMemo(
    () =>
      (jobs ?? [])
        .map((job) => job.jobId)
        .sort()
        .join("|"),
    [jobs]
  );

  useEffect(() => {
    // Set on cleanup so a request still in flight when the effect tears down
    // (unmount / job-set change) never calls setJobStatus afterwards.
    let cancelled = false;
    const jobIds = jobIdsKey ? jobIdsKey.split("|") : [];

    const pollStatuses = () => {
      jobIds.forEach(async (jobId) => {
        const external = externalStatus.current[jobId];

        // Seed our record from the caller's status the first time we see a job,
        // so a job that is already settled is skipped immediately.
        if (knownStatus.current[jobId] === undefined && external !== undefined) {
          knownStatus.current[jobId] = external;
        }
        // A re-run sets a fresh in-progress status on the caller's map; adopt it
        // so a previously-settled job resumes polling.
        if (isInProgressStatus(external) && external !== knownStatus.current[jobId]) {
          knownStatus.current[jobId] = external;
        }

        // Skip settled / unknown jobs. Keep polling only active runs and jobs
        // we haven't checked yet (status still undefined).
        const current = knownStatus.current[jobId];
        if (current !== undefined && !shouldPoll(current)) {
          return;
        }

        // Don't stack a second request while one is still in flight for this job.
        if (inFlight.current.has(jobId)) {
          return;
        }
        inFlight.current.add(jobId);

        try {
          const status = await getJobStatus(jobId);
          if (cancelled) return;
          consecutiveFailures.current[jobId] = 0;

          const previous = knownStatus.current[jobId];
          if (status === previous) {
            // No change — don't trigger a needless re-render.
            return;
          }
          knownStatus.current[jobId] = status;
          setJobStatus((prevState) => ({ ...prevState, [jobId]: status }));

          // AC5: payload (errorMessage / invalidItems / lastSuccessfulRunDate)
          // lives on the jobs-list query, not this status poll. Refetch it on
          // transition into any settled-with-payload state so those details
          // aren't stale.
          if (SETTLED_WITH_PAYLOAD.includes(status)) {
            queryClient.invalidateQueries("geoCalculations");
          }
        } catch {
          if (cancelled) return;
          // AC3: a failed status poll is not a success. Leave the known status as-is
          // instead of fabricating "Completed".
          if (knownStatus.current[jobId] === undefined) {
            const failures = (consecutiveFailures.current[jobId] ?? 0) + 1;
            consecutiveFailures.current[jobId] = failures;

            if (failures >= MAX_CONSECUTIVE_FAILURES) {
              // Stop polling this job — it's persistently failing and never had
              // a known status. Surface "Unknown" so the pill stops showing
              // "Checking…" forever.
              knownStatus.current[jobId] = "Unknown";
              setJobStatus((prevState) => ({ ...prevState, [jobId]: "Unknown" }));
            }
          }
        } finally {
          inFlight.current.delete(jobId);
        }
      });
    };

    // Fetch immediately so a fresh mount (e.g. returning to this page) shows the
    // real status right away instead of an empty map for the first interval.
    pollStatuses();
    const interval = setInterval(pollStatuses, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [jobIdsKey, setJobStatus, queryClient]);
}
